interface StrategyOption<T extends string> {
  value: T;
  label: string;
  description: string;
  icon?: string;
}

interface StrategySelectorProps<T extends string> {
  options: StrategyOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function StrategySelector<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
}: StrategySelectorProps<T>) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      role="radiogroup"
    >
      {options.map((option) => {
        const isSelected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`relative flex flex-col items-start rounded-lg border-2 p-4 text-left transition-colors ${
              isSelected
                ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {/* Checkmark badge */}
            {isSelected && (
              <span className="absolute top-2 right-2">
                <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </span>
            )}

            {/* Icon + Label */}
            <div className="flex items-center gap-2">
              {option.icon && (
                <span className="text-lg" aria-hidden="true">{option.icon}</span>
              )}
              <span className={`text-sm font-semibold ${
                isSelected
                  ? 'text-blue-800 dark:text-blue-200'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                {option.label}
              </span>
            </div>

            {/* Description */}
            <p className={`mt-1 text-xs leading-relaxed ${
              isSelected
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {option.description}
            </p>
          </button>
        );
      })}
    </div>
  );
}

export type { StrategyOption, StrategySelectorProps };
